<script lang="ts" setup>
/**
 * This view contains the tracking information for the event. Here we can track individual participants and their location.
 * Display the different tracks for the different events.
 *
 * NOTE: The google_maps_api_key is connected to technical@eqtiming.com account
 *
 * We use vue-google-map for a Google maps vue3 component
 * We need the Google Maps Typescript definitions
 * npm i -D @types/google.maps
 *
 *
 * @see https://vue3-google-map.com/getting-started/
 */
import { computed, ref, unref, watch } from 'vue';

import { CustomControl, CustomMarker, GoogleMap, Polyline } from 'vue3-google-map';
import { MapTrackData } from './MapTrackData';
import { randomstring } from '@/utilities/utils';
import { GeoFenceRectangle, MapPoint, MapTrack } from '@/objects/generated';
import { type IMapExposed } from './IMapExposed';
import { MapMarker } from './MapMarker';
import MapTrackMarker from './MapTrackMarker.vue';
import type { DropdownItemButton } from '@/objects';
import MapCustomControl from './MapCustomControl.vue';
import { generateHeatMapColor, normalizeHexColor } from '@/utilities/colorUtils';
import MapTrackLabel from './MapTrackLabel.vue';
import { GetAppsetting } from '@/composeables';
import { useDebouncer } from '@/utilities/Debouncer';
import { IconComp } from '..';
import type { MapZoomValue } from './MapZoomValue';
import { watchDebounced } from '@vueuse/core';

const props = withDefaults(
    defineProps<{
        /**
         * A list of tracks to display on the map
         */
        tracks: Array<MapTrackData>;

        /**
         * If we should automatically fit to the bounds of the tracks
         * This will focus the map on the tracks. This is the sum of all the points in all of the tracks
         * @default true
         */
        autobounds?: boolean;

        /**
         * A list of controls to display on the map in the top right corner
         */
        controls?: Array<DropdownItemButton>;

        /**
         * The center point of the map
         * If not provided, we will use the first point of the first track if it exists
         * @default null
         */
        centerpoint?: MapPoint | null;

        /**
         * We start the track at this distance
         * @default 0
         */
        minkm?: number;

        /**
         * We end the track at this distance
         * @default 99999999
         */
        maxkm?: number;

        /**
         * Show distance markers on the map in a white to black gradient to show what order the markers are in
         * @default true
         */
        gradientdistanceMarkers?: boolean;

        /**
         * The height of the map
         * @default 900
         */
        height?: number;

        /**
         * A list of markers to display on the map
         * @default []
         */
        mapmarkers?: Array<MapMarker>;

        /**
         * Show map labels. This shows road names, city names etc
         * @default false
         */
        showmaplabels?: boolean;

        /**
         * The default zoom level of the map
         * 0-19 where 0 is the world and 19 is street level
         * @default 7
         */
        zoom?: number;

        /**
         * Show the custom controls on the map
         * @default true
         */
        showCustomControls?: boolean;

        /**
         * Show searchbox on the map
         * @default false
         */
        showSearchBox?: boolean;

        /**
         * Hide the default controls on the map
         * @default false
         */
        hideDefaultControls?: boolean;

        /**
         * Will add a marker to the map when we click on the map, and emit the map-clicked event
         */
        clickToAddMarker?: boolean;

        /**
         * If we should hide the load spinner
         * @default false
         */
        hideProgress?: boolean;
    }>(),
    {
        minkm: 0,
        maxkm: 99999999,
        autobounds: true,
        centerpoint: null,
        gradientdistanceMarkers: true,
        showmaplabels: false,
        zoom: 7,
        showCustomControls: true,
        showSearchBox: false,
        hideDefaultControls: false,
        hideProgress: false,
    },
);

/**
 * Emitted events
 */
const emit = defineEmits<{
    /**
     * Emitted when the map is ready and rendered
     * @param val Should always be true
     */
    (e: 'ready', val: boolean): void;

    /**
     * Emitted when a track is clicked
     * @param track The track that was clicked
     */
    (e: 'track-clicked', track: MapTrackData, point: MapPoint): void;

    /**
     * Emitted when we click on a map point
     * @param point The point that was clicked
     */
    (e: 'map-clicked', point: MapPoint): void;

    /**
     * When a geofence has been changed
     */
    (e: 'geofenceUpdated', geofence: GeoFenceRectangle): void;
}>();

const geofencevalue = defineModel<GeoFenceRectangle | null>('geofence', { required: false, default: null });

// The reference to the map
const map = ref<typeof GoogleMap | null>(null);

const autocompleteInput = ref<HTMLInputElement | null>(null);
let autocomplete: google.maps.places.SearchBox;

// The reference to the Google Maps API
let api: google.maps.Map | null = null;

// Helper property for the center point
// We use this to center the map on the first point of the first track if it is not provided as a prop
let centerPoint: MapPoint | null = props.centerpoint;

// Additional tracks that can be added to the map
const additionalTracks = ref<Array<MapTrackData>>([]);
/**
 * A list of the markers that are displayed on the map
 */
const markers = ref<Array<MapMarker>>(props.mapmarkers ?? []);

/**
 * Show distance markers on the map
 */
const showDistanceMarkers = ref(true);

/**
 * If we should show the GeoFence or not.
 * @remarks This is only available if we have provided a geofence prop
 */
const showgeofence = ref(true);

/**
 * Show the track profile
 * TODO: This one is not implemented yet
 */
const showTrackProfile = ref(false);

/**
 * Internal list of tracks that are mapped to the correct format for the Polyline component
 * @returns Array of tracks
 */
const computedTracks = computed(() => {
    if (centerPoint == null && props.tracks && props.tracks.length > 0) {
        for (const t of props.tracks) {
            for (const p of t.MapTrack.Points) {
                if (centerPoint == null) {
                    centerPoint = p;
                    break;
                }
                if (centerPoint != null) break;
            }
        }
    }

    // Create the border tracks
    let borderTracks = props.tracks.map((t) => {
        let borderTrack = new MapTrackData(
            MapTrack.FromJsValues({
                ID: t.MapTrack.ID,
                LineColor: '#000000',
                LineWidth: t.MapTrack.LineWidth + 2,
                Points: t.MapTrack.Points,
            }),
        );
        borderTrack.Opacity = 0.8;
        return borderTrack;
    });

    borderTracks = borderTracks.concat(props.tracks);

    // Concat the tracks with the additional tracks
    let allTracks = borderTracks.concat(additionalTracks.value);

    return allTracks.map((t: MapTrackData) => ({
        geodesic: true,
        strokeColor: normalizeHexColor(t.MapTrack.LineColor),
        strokeOpacity: t.Opacity == 0 ? 1.0 : t.Opacity,
        strokeWeight: t.MapTrack.LineWidth == 0 ? 2 : t.MapTrack.LineWidth,
        clickable: true,
        trackid: t.Key,
        path: t.MapTrack.Points.filter(
            (x) => (x.DistanceFromStart ?? props.minkm) >= props.minkm && (x.DistanceFromStart ?? props.maxkm) <= props.maxkm,
        ).map((p) => ({
            lat: p.Latitude,
            lng: p.Longitude,
        })),
    }));
});

const computedProfileData = computed(() => {
    // TODO: Need to figure out how to use the ChartJS

    return '';
});

/**
 * We automatically add markers to the map for a given distance
 * The distance is calculated based on the distance from the start of the track and the total length of the track to get a resolution so we dont flood the map with markers
 */
const computedDistanceMarkers = computed(() => {
    if (!showDistanceMarkers.value) {
        return [];
    }

    let markers: MapMarker[] = [];
    let resolution = 1000;
    let decimals = 0;

    for (let track of props.tracks) {
        if (track.MapTrack.Points.length === 0) {
            continue;
        }

        let resolutionPoints: number[] = [];

        // We find the first point after the min distance
        let firstLargerThanMin: MapPoint | null = null;

        // We need a reversed copy to find the last point before the max distance
        let lastSmallerThanMax: MapPoint | null = null;

        // Build a new trackpoint source array
        let _trackpoints: MapPoint[] = [];
        for (let i = 0; i < track.MapTrack.Points.length; i++) {
            let p = track.MapTrack.Points[i];
            if (!firstLargerThanMin && (p.DistanceFromStart ?? 0) >= props.minkm) {
                firstLargerThanMin = p;
            }
            if ((p.DistanceFromStart ?? 0) <= props.maxkm) {
                lastSmallerThanMax = p;
            }

            if ((p.DistanceFromStart ?? props.minkm) >= props.minkm && (p.DistanceFromStart ?? props.maxkm) <= props.maxkm) {
                _trackpoints.push(p);
            }
        }

        // Calculate the length of the track so we know what resolution to use
        let lenghtOfTrack = (lastSmallerThanMax?.DistanceFromStart ?? 9999999) - (firstLargerThanMin?.DistanceFromStart ?? 0);

        // We set the resolution based on the length of the track
        if (lenghtOfTrack < 1000) {
            // Less than 1 km
            resolution = 100;
            decimals = 1;
        } else if (lenghtOfTrack < 7500) {
            // Less than 7.5 km
            resolution = 1000;
        } else if (lenghtOfTrack < 25000) {
            // Less than 25 km
            resolution = 2500;
        } else if (lenghtOfTrack < 100000) {
            // Less than 100 km
            resolution = 5000;
        } else if (lenghtOfTrack < 250000) {
            // Less than 250 km
            resolution = 10000;
        } else {
            resolution = 100000;
        }

        _trackpoints.forEach((point, index) => {
            // Add label to first point and every 1 km
            let currentDistance = Math.floor((point.DistanceFromStart ?? 0) / resolution);

            if (index === 0 || index == _trackpoints.length - 1 || !resolutionPoints.includes(currentDistance)) {
                if (index == _trackpoints.length - 1) {
                    decimals = 1;
                }

                let title = (Math.round((point.DistanceFromStart ?? 0) * 10) / 10 / 1000).toFixed(decimals) + ' km';

                const marker = new MapMarker();
                marker.Key = index;
                marker.Title = title;
                marker.Point = point;
                marker.Color = generateHeatMapColor(index, _trackpoints.length);
                marker.Type = 'square';
                marker.Height = 10;
                marker.Width = 30;
                marker.ZIndex = 999;

                markers.push(marker);
                resolutionPoints.push(currentDistance);
            }
        });
    }
    return markers;
});

/**
 * Property to check if there is no loaded tracks or no points in the tracks
 */
const isEmpty = ref(true);

/**
 * Check if the tracks are empty
 */
const isEmptyTracks = () => {
    if (computedTracks.value.length === 0) {
        return true;
    } else if (computedTracks.value.length === 1 && computedTracks.value[0].path.length === 0) {
        return true;
    } else if (computedTracks.value.length > 1) {
        // Find the sum of all Points array on each track
        let sum = computedTracks.value.reduce((acc, t) => acc + t.path.length, 0);
        return sum === 0;
    }
    return false;
};

/**
 * Sets the bounds of the map to fit all the tracks
 */
const setBounds = () => {
    const bounds = new google.maps.LatLngBounds();
    computedTracks.value.forEach((t) => {
        t.path.forEach((p) => {
            bounds.extend(p);
        });
    });

    api?.fitBounds(bounds);
};

/**
 * We add a new track to the map
 * @param track
 */
const addTrack = (track: MapTrackData) => {
    track.MapTrack.LineWidth = 2;
    // add the border color to the track
    let borderTrack = new MapTrackData(
        MapTrack.FromJsValues({
            ID: track.MapTrack.ID,
            LineColor: '#000000',
            LineWidth: track.MapTrack.LineWidth + 2,
            Points: track.MapTrack.Points,
        }),
    );
    borderTrack.Key = track.Key;
    borderTrack.Opacity = 0.5;
    additionalTracks.value.push(borderTrack);

    // We add the main track to the list of tracks
    track.Opacity = 1;
    additionalTracks.value.push(track);
};

/**
 * Remove a track from the map
 * @param track
 */
const removeTrack = (key: number) => {
    if (!additionalTracks.value.find((t) => t.Key === key)) {
        return;
    }

    additionalTracks.value = additionalTracks.value.filter((t) => t.Key !== key);
};

/**
 * Add a marker to the map
 * @param marker
 */
const addMarker = (marker: MapMarker) => {
    markers.value.push(marker);
};

/**
 * Focus on a marker on the map
 * @param key
 */
const focusOnMarker = (key: number) => {
    let marker = getMarker(key);
    if (marker == null) {
        return;
    }

    if (api) {
        api.setCenter({ lat: marker.Point?.Latitude!, lng: marker.Point?.Longitude! });
        api.setZoom(15);
    }
};

/**
 * Update a marker on the map
 * @param key
 * @param point
 */
const updateMarkerPosition = (key: number, point: MapPoint) => {
    let marker = getMarker(key);
    if (marker == null) {
        return;
    }

    // Check if the point is the same
    if (marker.Point?.Latitude === point.Latitude && marker.Point?.Longitude === point.Longitude) {
        return;
    }

    marker.Point = point;
};

/**
 * Return a marker from the list of markers
 * @param key
 */
const getMarker = (key: number) => {
    return markers.value.find((m) => m.Key === key) ?? null;
};

/**
 * Remove a marker from the map
 * @param key
 * @returns The removed marker
 */
const removeMarker = (key: number | null): MapMarker | null => {
    if (key == null) {
        return null;
    }
    let marker = markers.value.find((m) => m.Key === key);
    if (marker == null) {
        return null;
    }
    markers.value = markers.value.filter((m) => m.Key !== key);

    return marker;
};

/**
 * Clear all markers from the map
 */
const clearMarkers = () => {
    markers.value = [];
};

/**
 * Calculate the distance between two points
 * @param mk1
 * @param mk2
 */
const haversine_distance = (mk1: MapPoint, mk2: MapPoint): number => {
    if (mk1 == null || mk2 == null || mk1.Latitude == null || mk1.Longitude == null || mk2.Latitude == null || mk2.Longitude == null) {
        return 0;
    }
    let R = 6371071.03; // Radius of the Earth in meters
    let rlat1 = mk1.Latitude * (Math.PI / 180); // Convert degrees to radians
    let rlat2 = mk2.Latitude * (Math.PI / 180); // Convert degrees to radians
    let difflat = rlat2 - rlat1; // Radian difference (latitudes)
    let difflon = (mk2.Longitude - mk1.Longitude) * (Math.PI / 180); // Radian difference (longitudes)

    let d =
        2 *
        R *
        Math.asin(
            Math.sqrt(
                Math.sin(difflat / 2) * Math.sin(difflat / 2) +
                    Math.cos(rlat1) * Math.cos(rlat2) * Math.sin(difflon / 2) * Math.sin(difflon / 2),
            ),
        );
    return d;
};

/**
 * A list of what clusters each marker belong to
 */
let uniqueClusters = new Map<number, MapMarker[]>();

/**
 * Get a index in a cluster
 * @param key
 */
const getIndexInCluster = (key: number): number => {
    let cluster = uniqueClusters.get(key) ?? [];
    let index = cluster.findIndex((m) => m.Key === key);

    return index == -1 ? 0 : index;
};

/**
 * Builds a lookup collection of all the clusters. A cluster is defined as all the markers that are within 100 meters of each other
 * This mean that if there is less than 100m between each marker on the track, the entire track will be in the same cluster event ho there can be 99 meters between each marker on a line
 * TODO: Should we add a max distance between markers within the same cluster?
 * This is used to group markers that are close to each other.
 * @param m a list of markers
 */
const updateClusters = (m: MapMarker[]) => {
    // We disable this for now
    // TODO: This needs to be optimized. I think the math is ok, it is the problem with too many DOM changes on big datasets that is the problem. Probably because of vue computed and watches
    return;

    // let clusterMap = new Map<number, MapMarker[]>();
    // const fnsort = function (a: MapMarker, b: MapMarker) {
    //     return a.Key! - b.Key!;
    // };

    // m.forEach((marker, index) => {
    //     let cluster = clusterMap.get(marker.Key!);
    //     if (cluster == null) {
    //         cluster = [];
    //     }

    //     m.forEach((otherMarker, otherIndex) => {
    //         if (index !== otherIndex) {
    //             let distance = haversine_distance(marker.Point!, otherMarker.Point!);
    //             if (distance < 100) {
    //                 cluster.push(otherMarker);
    //             }
    //         }
    //     });

    //     clusterMap.set(marker.Key!, cluster);
    // });

    // // This is a combination of all the
    // for (let [key, cluster] of clusterMap.entries()) {
    //     uniqueClusters.set(key, cluster ?? []);

    //     for (let [otherKey, otherCluster] of clusterMap.entries()) {
    //         if (key !== otherKey) {
    //             let currentClusterPoints = uniqueClusters.get(key);

    //             // Check if any of the otherCluster points are in the currentCluster if they are, then add them to the currentCluster
    //             let anyExists =
    //                 currentClusterPoints?.length == 1 || otherCluster.some((x) => currentClusterPoints?.some((y) => y.Key === x.Key));

    //             if (anyExists) {
    //                 let total = currentClusterPoints?.concat(otherCluster) ?? [];

    //                 uniqueClusters.set(key, total.filter((x, i, a) => a.findIndex((t) => t.Key === x.Key) === i).sort(fnsort));
    //             }
    //         }
    //     }
    // }
};

/**
 * Watch the markers and update the clusters
 */
watchDebounced(markers.value, (m) => updateClusters(m), {
    debounce: 100,
});

/**
 * Watch the tracks and update the bounds
 */
watch(computedTracks, () => {
    setBounds();
    isEmpty.value = isEmptyTracks();

    if (isEmpty.value) setCenter();
});

/**
 * On ready watch. Initialize the api
 */
watch(
    () => map.value?.ready,
    (ready) => onReady(ready),
);

/**
 * If we have a geofence prop, we will watch it and add the geofence to the map
 */
watch(
    () => showgeofence.value,
    () => geoFenceRectangle?.setMap(showgeofence.value ? api : null),
);

/**
 * Watch the map tiles loaded status
 */
watch(
    () => map.value?.mapTilesLoaded,
    (status) => onAllLoaded(status),
);

/**
 * Set up the api when the map is ready
 * @param status
 */
const onReady = (status: boolean) => {
    if (status) {
        api = map.value?.map;

        // Set the map options
        const mapOptions = {
            mapTypeControl: props.hideDefaultControls ? false : true,
            mapTypeControlOptions: {
                style: google.maps.MapTypeControlStyle.HORIZONTAL_BAR,
                position: google.maps.ControlPosition.TOP_RIGHT,
            },
            zoomControl: props.hideDefaultControls ? false : true,
            zoomControlOptions: {
                position: google.maps.ControlPosition.RIGHT_BOTTOM,
            },
            scaleControl: true,
            streetViewControl: props.hideDefaultControls ? false : true,
            streetViewControlOptions: {
                position: google.maps.ControlPosition.RIGHT_BOTTOM,
            },
            fullscreenControl: props.hideDefaultControls ? false : true,
            fullscreenControlOptions: {
                position: google.maps.ControlPosition.TOP_RIGHT,
            },

            disableDefaultUI: props.hideDefaultControls,
            zoom: props.zoom,
            mapTypeId: props.showmaplabels ? 'hybrid' : 'satellite',
            draggableCursor: 'crosshair',
        };

        api?.setOptions(mapOptions);

        // Add the geo fence rectangle if it is provided
        if (geofencevalue.value != null) {
            addGeofence(geofencevalue.value);
        }

        if (autocompleteInput.value) {
            console.log('Initializing autocomplete');
            autocomplete = new window.google.maps.places.SearchBox(autocompleteInput.value);

            autocomplete.addListener('places_changed', () => {
                const places = autocomplete.getPlaces();
                if (places != undefined && places.length > 0) {
                    const location = places[0].geometry!.location!;
                    setCenter({ Latitude: location.lat(), Longitude: location.lng() } as MapPoint);
                }
            });
        }
    }
};

watch(geofencevalue, () => addGeofence(geofencevalue.value));

/**
 * When a track is clicked we emit it
 * @param track
 */
const onTrackClicked = (event: any, track: { trackid: number | null }) => {
    // We need to normalize the event latlng since we dont have the correct type
    let clickedMappoint = MapPoint.FromJsValues({
        Latitude: event.latLng.lat(),
        Longitude: event.latLng.lng(),
    });

    // Check if an additional track with this key exists
    let trackData = additionalTracks.value.find((t) => t.Key === track.trackid) as MapTrackData;
    emit('track-clicked', trackData, clickedMappoint);
};

let addedClickedMarker: MapMarker | null = null;

/**
 * When the map is clicked we emit it
 * @param event
 */
const onMapClicked = (event: any) => {
    if (!props.clickToAddMarker) {
        return;
    }

    // We need to normalize the event latlng since we dont have the correct type
    let clickedMappoint = MapPoint.FromJsValues({
        Latitude: event.latLng.lat(),
        Longitude: event.latLng.lng(),
    });

    if (addedClickedMarker != null) {
        // Remove the old marker
        removeMarker(addedClickedMarker.Key);
    }

    // Add a new marker to the map
    let marker = new MapMarker();
    marker.Key = markers.value.length + 1;
    marker.Point = clickedMappoint;
    marker.Type = 'pin';
    marker.Color = '#FF0000';
    marker.ZIndex = 1000;

    addedClickedMarker = marker;
    addMarker(marker);

    emit('map-clicked', clickedMappoint);
};

const isReady = ref(false);

/**
 * Is called when both the Google Maps API is loaded and the map tiles are loaded
 *
 * @param status
 * @emits ready
 */
const onAllLoaded = (status: boolean) => {
    if (!status) return;
    isReady.value = true;

    setCenter();

    addGeofence(geofencevalue.value);

    emit('ready', true);
};

/**
 * Sets the center of the map
 * If no point is provided, we use the first point of the first track if it exists or else we use a default point
 * @param point
 */
const setCenter = (point?: MapPoint) => {
    point = point ?? centerPoint ?? ({ Latitude: 59.913868, Longitude: 10.752245 } as MapPoint);
    if (api) {
        api.setCenter({ lat: point.Latitude, lng: point.Longitude });
        api.setZoom(props.zoom ?? (centerPoint != null ? 10 : 6));
    }
};

/**
 * Generate a random guid for the map
 */
const guid = randomstring('map-', '-');

const mapwrapper = ref<HTMLElement | null>(null);

/**
 * The rectangle object we use for the geofence
 */
let geofenceInitialized = false;
let geoFenceRectangle: google.maps.Rectangle | null = null;

const geofenceSaveDebouncer = useDebouncer();

const addGeofence = (geofence: GeoFenceRectangle | null) => {
    if (geofencevalue.value == null || geofence == null || geofence.TopLeft == null || geofence.BottomRight == null) {
        geoFenceRectangle?.setMap(null);
        return;
    }

    // Initialize the geofence rectangle if it is not already initialized
    if (geoFenceRectangle == null) {
        geoFenceRectangle = new google.maps.Rectangle({
            bounds: {
                north: 0,
                south: 0,
                east: 0,
                west: 0,
            },
            editable: true,
            draggable: true,
            fillOpacity: 0.05,
            fillColor: '#000099',
            strokeWeight: 1,
            strokeColor: '#0000ff',
        });
    }

    geoFenceRectangle?.setBounds({
        north: geofencevalue.value.TopLeft!.Latitude,
        south: geofencevalue.value.BottomRight!.Latitude,
        east: geofencevalue.value.BottomRight!.Longitude,
        west: geofencevalue.value.TopLeft!.Longitude,
    });

    geoFenceRectangle?.setMap(api);

    if (!geofenceInitialized) {
        geofenceInitialized = true;
        // listen to changes
        ['bounds_changed', 'dragend'].forEach((eventName) => {
            geoFenceRectangle?.addListener(eventName, () => {
                /**
                 * Updates the v-model geofencevalue after a timeout to avoid too many updates while dragging the rectangle
                 */
                geofenceSaveDebouncer(() => {
                    console.log('Geofence rectangle changed, updating geofence value');
                    if (geofencevalue.value == null || geofencevalue.value.TopLeft == null || geofencevalue.value.BottomRight == null) {
                        return;
                    }

                    geofencevalue.value.TopLeft.Latitude = geoFenceRectangle?.getBounds()?.getNorthEast().lat() ?? 0;
                    geofencevalue.value.TopLeft.Longitude = geoFenceRectangle?.getBounds()?.getSouthWest().lng() ?? 0;
                    geofencevalue.value.BottomRight.Latitude = geoFenceRectangle?.getBounds()?.getSouthWest().lat() ?? 0;
                    geofencevalue.value.BottomRight.Longitude = geoFenceRectangle?.getBounds()?.getNorthEast().lng() ?? 0;

                    emit('geofenceUpdated', geofencevalue.value);
                }, 1000);
            });
        });
    }
};

const getCenter = (): MapPoint | null => {
    if (api == null) {
        return null;
    }

    const center = api.getCenter();

    if (center == null) {
        return null;
    }
    return MapPoint.FromJsValues({ Latitude: center.lat(), Longitude: center.lng() });
};

/**
 * Set the zoom level of the map
 * @param zoom
 */
const setZoom = (zoom: MapZoomValue) => {
    if (api == null) {
        return;
    }

    // If the zoom is a string, we need to convert it to a number
    if (typeof zoom === 'string') {
        switch (zoom) {
            case 'world':
                zoom = 2;
                break;
            case 'continent':
                zoom = 3;
                break;
            case 'country':
                zoom = 7;
                break;
            case 'state':
                zoom = 10;
                break;
            case 'county':
                zoom = 12;
                break;
            case 'city':
                zoom = 15;
                break;
            case 'street':
                zoom = 19;
                break;
            default:
                zoom = 7; // Default to 7 if unknown
        }
    }

    setTimeout(() => {
        api!.setZoom(zoom);
    }, 100);
};

/**
 * Watch the centerpoint prop and set the center of the map
 */
watch(
    () => props.centerpoint,
    (newVal) => {
        centerPoint = newVal;
        setCenter(newVal ?? undefined);
    },
);

defineExpose<IMapExposed>({
    AddTrack: addTrack,
    RemoveTrack: removeTrack,
    AddMarker: addMarker,
    GetMarker: getMarker,
    RemoveMarker: removeMarker,
    ClearMarkers: clearMarkers,
    UpdateMarkerPosition: updateMarkerPosition,
    FocusOnMarker: focusOnMarker,
    GetCenter: getCenter,
    SetZoom: setZoom,
});
</script>
<template>
    <div
        ref="mapwrapper"
        class="h-100"
    >
        <div
            class="h-100 w-100 p-5 border d-flex justify-content-center align-items-center border-opacity-50"
            v-if="!isReady && !hideProgress"
        >
            <IconComp
                icon="fa-spinner-third"
                class="fa-spin mb-2 text-info"
                size="2x"
            />
        </div>

        <div :style="{ visibility: isReady ? 'visible' : 'hidden', height: '100%' }">
            <GoogleMap
                class="mapcomp"
                ref="map"
                :api-key="GetAppsetting('google_maps_api_key')"
                :style="{ width: '100%', height: !props.height ? '100%' : props.height + 'px' }"
                :mapId="guid"
                @click="onMapClicked"
            >
                <template #default>
                    <!-- Set up a list of custom controls in the top right corner of the map -->
                    <CustomControl
                        v-if="isReady && props.showCustomControls"
                        position="TOP_RIGHT"
                        class="customcontrol gmnoprint gm-style-mtc-bbw d-flex"
                    >
                        <MapCustomControl
                            v-for="(control, i) in props.controls"
                            :key="i"
                            type="checkbox"
                            :title="unref(control.translationkey ?? '')"
                            @click="() => control.action()"
                        />

                        <MapCustomControl
                            type="checkbox"
                            title="map.showdistancemarkers"
                            v-model="showDistanceMarkers"
                        />

                        <MapCustomControl
                            v-if="geofencevalue != null"
                            type="checkbox"
                            title="map.showgeofence"
                            v-model="showgeofence"
                        />
                        <!-- <MapCustomControl
                    type="checkbox"
                    title="map.showtrackprofile"
                    v-model="showTrackProfile"
                /> -->
                    </CustomControl>

                    <CustomControl
                        v-if="props.showSearchBox"
                        position="TOP_LEFT"
                        class="customcontrol gmnoprint gm-style-mtc-bbw d-flex w-50"
                    >
                        <!-- Search box -->
                        <input
                            ref="autocompleteInput"
                            type="text"
                            :placeholder="$tm('map.searchboxplaceholder')"
                            class="search-box w-100 p-2 fw-normal fs-6 fw-normal"
                        />
                    </CustomControl>

                    <Polyline
                        v-for="(track, i) in computedTracks"
                        :key="i"
                        :options="track"
                        @click="onTrackClicked($event, track)"
                    />

                    <template v-if="markers.length > 0">
                        <CustomMarker
                            v-for="(marker, i) in markers"
                            :key="i"
                            :options="{
                                position: { lat: marker.Point?.Latitude!, lng: marker.Point?.Longitude! },
                                zIndex: marker.ZIndex,
                                anchorPoint: marker.Type == 'pin' ? 'BOTTOM_CENTER' : undefined,
                            }"
                            @click="
                                () => {
                                    if (marker.onClick) {
                                        marker.onClick();
                                    }
                                }
                            "
                            ><MapTrackMarker
                                v-if="marker.Type === 'circle' || marker.Type === 'square' || marker.Type === 'pin'"
                                :color="marker.Color"
                                :type="marker.Type"
                                :height="marker.Height"
                                :width="marker.Width"
                                :index="getIndexInCluster(marker.Key!)"
                                :label="marker.Title"
                                :zindex="marker.ZIndex"
                                :strokecolor="marker.StrokeColor"
                                :opacity="marker.Visible ? 1 : 0"
                                :fontfactor="marker.FontFactor"
                            />

                            <MapTrackLabel
                                v-else
                                :label="marker.Title"
                            />
                        </CustomMarker>
                    </template>

                    <template v-if="computedDistanceMarkers.length > 0">
                        <CustomMarker
                            v-for="(marker, i) in computedDistanceMarkers"
                            :key="i"
                            :options="{ position: { lat: marker.Point?.Latitude!, lng: marker.Point?.Longitude! }, zIndex: 100 }"
                        >
                            <span
                                class="badge opacity-75"
                                :style="{
                                    'background-color': marker.Color,
                                }"
                                >{{ marker.Title }}</span
                            >
                        </CustomMarker>
                    </template>
                </template>
            </GoogleMap>
        </div>
    </div>
</template>
